import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import {
  ArrowLeft,
  MoreHorizontal,
  MapPin,
  Link as LinkIcon,
  Calendar,
  Grid3X3,
  Play,
  Music2,
  Video,
  Bookmark,
  Heart,
  MessageCircle,
  Share2,
  UserPlus,
  UserCheck,
  Settings,
  Users,
  User,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useUserProfile, useFollowUser, useBookmarksFeed, useLikedFeed } from '@/hooks/useUser';
import { useProfileFeed, useProfileSongs, useProfileImages } from '@/hooks/useFeed';
import { useLikePost, useBookmarkPost } from '@/hooks/usePost';
import { formatCount } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import EditProfile from '@/components/EditProfile';
import FollowList from '@/components/FollowList';
import PostCard, { getPostImage, getLikesCount } from '@/components/PostCard';
import type { Post } from '@/types';

interface ProfileProps {
  onBack?: () => void;
  isOwnProfile?: boolean;
  username?: string;
}

export default function Profile({ onBack, isOwnProfile = true, username: usernameProp }: ProfileProps) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'posts' | 'media' | 'likes' | 'saved'>('posts');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [followListMode, setFollowListMode] = useState<'followers' | 'following' | null>(null);
  const [likeOverrides, setLikeOverrides] = useState<Record<string, { liked: boolean; count: number }>>({});
  const [bookmarkOverrides, setBookmarkOverrides] = useState<Record<string, boolean>>({});
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Determine whose profile to show
  const profileUsername = isOwnProfile ? currentUser?.username : usernameProp;

  // Fetch other user's profile (skip if own profile)
  const { data: otherProfile } = useUserProfile(isOwnProfile ? undefined : profileUsername);

  // The profile to display
  const profile = isOwnProfile ? currentUser : otherProfile;

  const engagement = profile ? ((profile as any).ProfileEngagement || (profile as any).profileEngagement) : null;

  const isFollowing = engagement ? (engagement.isFollowing || false) : false;
  //const isFollowingYou = engagement ? (engagement.isFollowingYou || false) : false;

  // Feed queries for profile tabs
  const { data: postsData, isLoading: isPostsLoading } = useProfileFeed(profileUsername ?? '');
  const { data: songsData } = useProfileSongs(profileUsername ?? '');
  const { data: imagesData } = useProfileImages(profileUsername ?? '');

  const followMutation = useFollowUser();
  const likeMutation = useLikePost();
  const bookmarkMutation = useBookmarkPost();

  // Bookmarks + liked feeds (own profile only)
  const { data: bookmarksData } = useBookmarksFeed(isOwnProfile);
  const { data: likedData } = useLikedFeed(profileUsername);

  // Flatten feed pages
  const userPosts = postsData?.pages.flatMap((p) => p.posts) ?? [];
  const userSongs = songsData?.pages.flatMap((p) => p.posts) ?? [];
  const userImages = imagesData?.pages.flatMap((p) => p.posts) ?? [];
  const userBookmarks = bookmarksData?.pages.flatMap((p) => (p as Record<string, unknown>).posts as Post[] ?? []) ?? [];
  const userLiked = likedData?.pages.flatMap((p) => (p as Record<string, unknown>).posts as Post[] ?? []) ?? [];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' });
      gsap.fromTo(contentRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out', delay: 0.1 });
      if (tabsRef.current) {
        gsap.fromTo(tabsRef.current.children, { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.3, stagger: 0.05, ease: 'power2.out', delay: 0.2 });
      }
    });
    return () => ctx.revert();
  }, []);

  const handleFollow = () => {
    if (!profileUsername) return;
    followMutation.mutate({ username: profileUsername, isFollowing });
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'posts': return <Grid3X3 size={18} />;
      case 'media': return <Play size={18} />;
      case 'likes': return <Heart size={18} />;
      case 'saved': return <Bookmark size={18} />;
      default: return <Grid3X3 size={18} />;
    }
  };

  const getCurrentPosts = (): Post[] => {
    switch (activeTab) {
      case 'posts': return userPosts;
      case 'media': return [...userImages, ...userSongs];
      case 'likes': return userLiked;
      case 'saved': return userBookmarks;
      default: return userPosts;
    }
  };

  const handleLike = (post: Post) => {
    const postId = post.id;
    const override = likeOverrides[postId];
    const currentLiked = override?.liked ?? (post.postEngagement?.liked ?? false);
    const currentCount = override?.count ?? getLikesCount(post);

    // Optimistic update
    setLikeOverrides(prev => ({
      ...prev,
      [postId]: {
        liked: !currentLiked,
        count: currentLiked ? Math.max(0, currentCount - 1) : currentCount + 1,
      },
    }));

    likeMutation.mutate(
      { ulid: postId, liked: currentLiked },
      {
        onError: () => {
          // Revert on error
          setLikeOverrides(prev => ({
            ...prev,
            [postId]: { liked: currentLiked, count: currentCount },
          }));
        },
      }
    );
  };

  const handleBookmark = (post: Post) => {
    const postId = post.id;
    const currentBookmarked = bookmarkOverrides[postId] ?? post.postEngagement?.bookmarked ?? (post as Record<string, unknown>).isBookmarked ?? false;

    // Optimistic update
    setBookmarkOverrides(prev => ({
      ...prev,
      [postId]: !currentBookmarked,
    }));

    bookmarkMutation.mutate(
      { ulid: postId, bookmarked: currentBookmarked },
      {
        onError: () => {
          // Revert on error
          setBookmarkOverrides(prev => ({
            ...prev,
            [postId]: currentBookmarked,
          }));
        },
      }
    );
  };

  const renderMediaGrid = (posts: Post[]) => {
    return (
      <div className="grid grid-cols-3 gap-1">
        {posts.map((post) => {
          const img = getPostImage(post);
          const postType = post.type;

          return (
            <div
              key={post.id}
              onClick={() => navigate(`/post/${post.id}`)}
              className="relative aspect-square bg-[#141821] overflow-hidden cursor-pointer group"
            >
              {img ? (
                <img
                  src={img}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#1A1F2A]">
                  <User size={24} className="text-[#A7B0C8]" />
                </div>
              )}

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                <div className="flex items-center gap-1 text-white">
                  <Heart size={16} fill="white" />
                  <span className="text-sm font-medium">{formatCount(getLikesCount(post))}</span>
                </div>
                <div className="flex items-center gap-1 text-white">
                  <MessageCircle size={16} />
                  <span className="text-sm font-medium">{formatCount(post.commentCount ?? 0)}</span>
                </div>
              </div>

              {/* Type indicator */}
              {(postType === 'SHORT' || postType === 'BURST') && (
                <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
                  <Play size={12} fill="white" className="text-white" />
                </div>
              )}
              {postType === 'VIDEO' && (
                <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
                  <Video size={12} className="text-white" />
                </div>
              )}
              {postType === 'SONG' && (
                <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1">
                  <Music2 size={12} className="text-white" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTabContent = () => {
    const posts = getCurrentPosts();

    if (isPostsLoading && activeTab === 'posts') {
      return (
        <div className="flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-[#A38BFF]" />
        </div>
      );
    }

    if (posts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-[#141821] flex items-center justify-center mb-4">
            {getTabIcon(activeTab)}
          </div>
          <p className="text-[#A7B0C8] text-lg">No {activeTab} yet</p>
          <p className="text-sm text-[#A7B0C8] mt-1">
            {activeTab === 'saved' ? 'Save posts to see them here' : 'Content will appear here'}
          </p>
        </div>
      );
    }

    // Use grid for media tab, post cards for everything else
    if (activeTab === 'media') {
      return renderMediaGrid(posts);
    }

    return (
      <div className="px-4 pt-2">
        {posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            liked={likeOverrides[post.id]?.liked}
            likeCount={likeOverrides[post.id]?.count}
            bookmarked={bookmarkOverrides[post.id]}
            onLike={handleLike}
            onBookmark={handleBookmark}
            onComment={(id) => navigate(`/post/${id}`)}
          />
        ))}
      </div>
    );
  };

  // Profile data with fallbacks
  const displayName = (profile as Record<string, unknown>)?.displayName as string
    || (profile as Record<string, unknown>)?.name as string
    || profile?.username
    || 'User';
  const avatarUrl = processAvatarUrl(profile?.avatar) || '';
  const coverImage = (profile as Record<string, unknown>)?.background as string
    || (profile as Record<string, unknown>)?.backgroundImage as string
    || '';
  const bio = (profile as Record<string, unknown>)?.bio as string || '';
  const location = (profile as Record<string, unknown>)?.location as string || '';
  const website = (profile as Record<string, unknown>)?.website as string || '';
  const followersCount = (profile as Record<string, unknown>)?.followersCount as number ?? 0;
  const followingCount = (profile as Record<string, unknown>)?.followingCount as number ?? 0;
  const postsCount = (profile as Record<string, unknown>)?.postsCount as number ?? 0;

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Header with Cover Image */}
      <div ref={headerRef} className="relative">
        {/* Cover Image */}
        <div className="h-40 bg-gradient-to-b from-[#1A1F2A] to-[#141821] overflow-hidden">
          {coverImage ? (
            <img src={coverImage} alt="Cover" className="w-full h-full object-cover opacity-60" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#A38BFF]/20 to-[#7B68EE]/10" />
          )}
        </div>

        {/* Top Navigation */}
        <div className="absolute top-0 left-0 right-0 px-4 pb-3" 
          style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
          <div className="flex items-center justify-between">
            {onBack && (
              <button
                onClick={onBack}
                className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              >
                <ArrowLeft size={22} />
              </button>
            )}
            <div className="flex items-center gap-2">
              {isOwnProfile ? (
                <button
                  aria-label="Settings"
                  onClick={() => navigate('/settings')}
                  className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
                >
                  <Settings size={20} />
                </button>
              ) : (
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
                >
                  <MoreHorizontal size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Profile Info Card */}
        <div className="px-4 -mt-16 relative z-10">
          <div className="bg-[#141821] rounded-2xl p-4 border border-white/[0.06]">
            {/* Avatar and Actions */}
            <div className="flex items-start justify-between">
              <div className="relative -mt-12">
                <div className="w-24 h-24 rounded-full p-[3px] bg-gradient-to-br from-[#A38BFF] to-[#7B68EE]">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} className="w-full h-full rounded-full object-cover border-2 border-[#141821]" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-[#141821] border-2 border-[#141821] flex items-center justify-center">
                      <User size={32} className="text-[#A38BFF]" />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 mt-2">
                {isOwnProfile ? (
                  <button
                    onClick={() => setShowEditProfile(true)}
                    className="h-10 px-4 bg-[#0B0D10] rounded-full text-sm font-medium text-[#F5F7FF] border border-white/[0.08] hover:bg-[#1A1F2A] transition-colors flex items-center justify-center"
                  >
                    Edit Profile
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleFollow}
                      disabled={followMutation.isPending}
                      className={`h-10 px-4 rounded-full text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                        isFollowing
                          ? 'bg-[#0B0D10] text-[#F5F7FF] border border-white/[0.08]'
                          : 'bg-[#A38BFF] text-[#0B0D10]'
                      }`}
                    >
                      {followMutation.isPending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : isFollowing ? (
                        <><UserCheck size={16} /><span>Following</span></>
                      ) : (
                        <><UserPlus size={16} /><span>Follow</span></>
                      )}
                    </button>
                    <button className="h-10 px-4 bg-[#0B0D10] rounded-full text-sm font-medium text-[#F5F7FF] border border-white/[0.08] flex items-center justify-center">
                      Message
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Name and Username */}
            <div className="mt-3">
              <h1 className="text-xl font-bold text-[#F5F7FF] flex items-center gap-2">
                {displayName}
              </h1>
              <p className="text-sm text-[#A7B0C8]">@{profile?.username || ''}</p>
            </div>

            {/* Bio */}
            {bio && (
              <p className="mt-3 text-sm text-[#F5F7FF] whitespace-pre-line">{bio}</p>
            )}

            {/* Details */}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#A7B0C8]">
              {location && (
                <div className="flex items-center gap-1">
                  <MapPin size={14} />
                  <span>{location}</span>
                </div>
              )}
              {website && (
                <div className="flex items-center gap-1">
                  <LinkIcon size={14} />
                  <a href={website.startsWith('http') ? website : `https://${website}`} className="text-[#A38BFF] hover:underline">
                    {website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Calendar size={14} />
                <span>Member</span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/[0.06]">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-[#F5F7FF]">{formatCount(postsCount)}</span>
                <span className="text-sm text-[#A7B0C8]">Posts</span>
              </div>
              <button
                onClick={() => setFollowListMode('followers')}
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              >
                <span className="font-bold text-[#F5F7FF]">{formatCount(followersCount)}</span>
                <span className="text-sm text-[#A7B0C8]">Followers</span>
              </button>
              <button
                onClick={() => setFollowListMode('following')}
                className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              >
                <span className="font-bold text-[#F5F7FF]">{formatCount(followingCount)}</span>
                <span className="text-sm text-[#A7B0C8]">Following</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div
        ref={tabsRef}
        className="flex items-center justify-around mt-4 border-b border-white/[0.06] sticky top-0 bg-[#0B0D10]/95 backdrop-blur-xl z-30"
      >
        {(['posts', 'media', 'likes', ...(isOwnProfile ? ['saved'] : [])] as ('posts' | 'media' | 'likes' | 'saved')[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === tab ? 'text-[#A38BFF]' : 'text-[#A7B0C8] hover:text-[#F5F7FF]'
            }`}
          >
            {getTabIcon(tab)}
            <span className="capitalize">{tab}</span>
            {activeTab === tab && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#A38BFF]" />
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div ref={contentRef} className="mt-2">
        {renderTabContent()}
      </div>

      {/* More Menu Modal */}
      {showMoreMenu && (
        <div
          className="fixed inset-0 z-50 bg-black/60"
          onClick={() => setShowMoreMenu(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-[#141821] rounded-t-[24px] p-4"
            style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
            </div>
            <div className="space-y-2">
              <button className="w-full py-3 px-4 bg-[#0B0D10] rounded-xl text-left text-[#F5F7FF] flex items-center gap-3">
                <Share2 size={18} />
                <span>Share Profile</span>
              </button>
              <button className="w-full py-3 px-4 bg-[#0B0D10] rounded-xl text-left text-[#F5F7FF] flex items-center gap-3">
                <Users size={18} />
                <span>Add to Group</span>
              </button>
              <button className="w-full py-3 px-4 bg-[#0B0D10] rounded-xl text-left text-[#FF4D4D] flex items-center gap-3">
                <span>Block User</span>
              </button>
              <button className="w-full py-3 px-4 bg-[#0B0D10] rounded-xl text-left text-[#FF4D4D] flex items-center gap-3">
                <span>Report</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile sheet */}
      {showEditProfile && (
        <EditProfile onClose={() => setShowEditProfile(false)} />
      )}

      {/* Followers / Following list */}
      {followListMode && profileUsername && (
        <FollowList
          username={profileUsername}
          mode={followListMode}
          onClose={() => setFollowListMode(null)}
        />
      )}
    </div>
  );
}
